import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp, api } from "@/context/AppContext";
import { toast } from "sonner";
import { Plus, Copy, Check, Upload, ExternalLink, Pencil, Trash2, ShieldCheck, Link2 } from "lucide-react";

export default function Dashboard() {
  const { lang, t, owner, setOwner } = useApp();
  const navigate = useNavigate();
  const head = lang === "hi" ? "font-hi" : "font-display";
  const [sarees, setSarees] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState(false);
  const [waNumber, setWaNumber] = useState("");
  const [waBusy, setWaBusy] = useState(false);

  const reload = async () => {
    const [s, a] = await Promise.all([api.get("/sarees/mine"), api.get("/analytics")]);
    setSarees(s.data);
    setStats(a.data);
  };

  useEffect(() => {
    if (!owner) { navigate("/login"); return; }
    setWaNumber(owner.whatsapp_number || "");
    reload();
  }, [owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const storeUrl = owner ? `${window.location.origin}/store/${owner.store_slug}` : "";
  const copy = async () => {
    await navigator.clipboard.writeText(storeUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  const updateWa = async () => {
    setWaBusy(true);
    try {
      const r = await api.patch("/auth/me", { whatsapp_number: waNumber });
      setOwner(r.data); toast.success(t.dash.whatsappSaved);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setWaBusy(false); }
  };

  const onAdded = () => { setShowAdd(false); reload(); };
  const onEdited = () => { setEditing(null); reload(); };

  const remove = async (s) => {
    if (!window.confirm(t.dash.confirmDelete)) return;
    try { await api.delete(`/sarees/${s.id}`); toast.success(t.dash.deleted); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyTryonLink = async (s) => {
    const link = `${window.location.origin}/tryon/${s.id}`;
    await navigator.clipboard.writeText(link);
    toast.success(t.dash.linkCopied);
  };

  const widgetSrc = owner ? `${process.env.REACT_APP_BACKEND_URL}/api/widget.js?store=${owner.store_slug}` : "";
  const widgetSnippet = `<script src="${widgetSrc}" defer></script>`;
  const [snippetCopied, setSnippetCopied] = useState(false);
  const copySnippet = async () => {
    await navigator.clipboard.writeText(widgetSnippet);
    setSnippetCopied(true);
    setTimeout(() => setSnippetCopied(false), 1800);
    toast.success(t.dash.widgetCopied);
  };

  if (!owner) return null;

  const statCards = [
    { k: "tryons", v: stats?.total_tryons ?? 0, label: t.dash.stats.tryons },
    { k: "buys", v: stats?.total_buy_clicks ?? 0, label: t.dash.stats.buys },
    { k: "conv", v: `${stats?.conversion_rate ?? 0}%`, label: t.dash.stats.conv },
    { k: "last7", v: stats?.tryons_last_7_days ?? 0, label: t.dash.stats.last7 },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-16" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase font-semibold text-[#C9A84C]">✦ {owner.store_name}</div>
          <h1 className={`${head} text-3xl sm:text-5xl text-[#2D1820] font-light tracking-tight mt-2`} data-testid="dashboard-title">
            {t.dash.title}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {owner.is_admin && (
            <Link to="/admin" className="btn-secondary inline-flex items-center gap-2 self-start" data-testid="admin-link">
              <ShieldCheck size={14} /> Admin
            </Link>
          )}
          <button onClick={() => setShowAdd(true)} className="btn-primary inline-flex items-center gap-2 self-start sm:self-auto" data-testid="add-saree-btn">
            <Plus size={16} /> {t.dash.add}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-grid">
        {statCards.map((s) => (
          <div key={s.k} className="bg-white border border-[#E8DFDC] p-5" data-testid={`stat-${s.k}`}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{s.label}</div>
            <div className={`${head} text-3xl sm:text-4xl text-[#6B1F3A] font-light mt-2`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#FCFAF8] border border-[#E8DFDC] p-5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.storeLink}</div>
          <div className="mt-2 flex items-center gap-2">
            <Link to={`/store/${owner.store_slug}`} className="text-[#6B1F3A] text-sm break-all hover:underline flex items-center gap-1" data-testid="store-link">
              {storeUrl} <ExternalLink size={12} />
            </Link>
            <button onClick={copy} className="ml-auto text-xs tracking-widest uppercase border border-[#E8DFDC] px-3 py-2 hover:border-[#C9A84C]" data-testid="copy-store-link-btn">
              {copied ? <span className="inline-flex items-center gap-1"><Check size={12}/> {t.dash.copied}</span> : <span className="inline-flex items-center gap-1"><Copy size={12}/> {t.dash.copy}</span>}
            </button>
          </div>
        </div>
        <div className="bg-[#FCFAF8] border border-[#E8DFDC] p-5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.whatsappLabel}</div>
          <div className="mt-2 flex items-center gap-2">
            <input value={waNumber} onChange={(e) => setWaNumber(e.target.value)} placeholder="+919876543210" className="field" data-testid="whatsapp-input" />
            <button onClick={updateWa} disabled={waBusy} className="btn-primary !py-3 !px-5 text-[11px]" data-testid="whatsapp-save-btn">
              {waBusy ? "…" : t.dash.updateWhatsapp}
            </button>
          </div>
        </div>
      </div>

      {/* Shopify widget snippet */}
      <div className="mt-6 border border-[#E8DFDC] bg-[#FCFAF8] p-5" data-testid="shopify-widget-card">
        <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-semibold text-[#6B1F3A]">
          <span aria-hidden>🛍️</span> {t.dash.widgetTitle}
        </div>
        <p className="mt-2 text-sm text-[#635057] leading-relaxed max-w-3xl">{t.dash.widgetSub}</p>
        <div className="mt-4 bg-white border border-[#E8DFDC] p-3 sm:p-4 font-mono text-[11px] sm:text-xs break-all text-[#2D1820]" data-testid="widget-snippet">
          {widgetSnippet}
        </div>
        <button
          onClick={copySnippet}
          className="mt-3 inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-[#6B1F3A] text-[#6B1F3A] px-4 py-2 hover:bg-[#6B1F3A] hover:text-white transition-colors"
          data-testid="copy-widget-snippet-btn"
        >
          {snippetCopied ? <><Check size={12}/> {t.dash.widgetCopied}</> : <><Copy size={12}/> {t.dash.widgetCopy}</>}
        </button>
      </div>

      {/* Top sarees */}
      {stats?.top_sarees?.length > 0 && (        <div className="mt-10">
          <div className="text-xs tracking-[0.3em] uppercase font-semibold text-[#C9A84C]">✦ {t.dash.stats.topTitle}</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="top-sarees-grid">
            {stats.top_sarees.map((s, i) => (
              <div key={s.id} className="border border-[#E8DFDC] bg-white relative" data-testid={`top-saree-${s.id}`}>
                <div className="aspect-[3/4] overflow-hidden bg-[#FCFAF8]">
                  <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="text-xs font-medium text-[#2D1820] truncate">{s.name}</div>
                  <div className="text-[10px] tracking-widest text-[#635057] mt-1">{s.tryon_count} {t.dash.stats.tryons.toLowerCase()}</div>
                </div>
                <div className="absolute top-2 left-2 bg-[#6B1F3A] text-white text-[10px] tracking-widest px-2 py-1 font-semibold">#{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sarees catalog */}
      <div className="mt-10">
        <div className="text-xs tracking-[0.3em] uppercase font-semibold text-[#C9A84C] mb-4">✦ {t.dash.stats.sarees}</div>
        {sarees.length === 0 ? (
          <div className="border border-dashed border-[#E8DFDC] p-12 text-center text-[#635057]" data-testid="dashboard-empty">
            <div className="text-3xl mb-3">🥻</div><div>{t.dash.empty}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {sarees.map((s) => (
              <div key={s.id} className="card-product group" data-testid={`my-saree-${s.id}`}>
                <div className="aspect-[3/4] overflow-hidden bg-[#FCFAF8]">
                  <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-4">
                  <div className="text-[#2D1820] font-medium text-sm">{s.name}</div>
                  <div className="text-xs text-[#635057] mt-1">{s.fabric}</div>
                  <div className="text-[#6B1F3A] font-semibold mt-2">₹ {Number(s.price).toLocaleString("en-IN")}</div>
                  <button
                    onClick={() => copyTryonLink(s)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#E8DFDC] hover:border-[#C9A84C] hover:text-[#6B1F3A] text-[10px] tracking-[0.2em] uppercase font-semibold py-2 transition-colors"
                    data-testid={`copy-tryon-link-${s.id}`}
                  >
                    <Link2 size={12} /> {t.dash.copyLink}
                  </button>
                </div>
                <div className="absolute inset-x-0 top-0 p-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditing(s)} className="bg-white/95 backdrop-blur p-2 border border-[#E8DFDC] hover:border-[#C9A84C]" data-testid={`edit-saree-${s.id}`} aria-label="Edit">
                    <Pencil size={14} className="text-[#6B1F3A]" />
                  </button>
                  <button onClick={() => remove(s)} className="bg-white/95 backdrop-blur p-2 border border-[#E8DFDC] hover:border-[#9B2226]" data-testid={`delete-saree-${s.id}`} aria-label="Delete">
                    <Trash2 size={14} className="text-[#9B2226]" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && <SareeModal mode="add" onClose={() => setShowAdd(false)} onSaved={onAdded} />}
      {editing && <SareeModal mode="edit" saree={editing} onClose={() => setEditing(null)} onSaved={onEdited} />}
    </div>
  );
}

function SareeModal({ mode, saree, onClose, onSaved }) {
  const { lang, t } = useApp();
  const head = lang === "hi" ? "font-hi" : "font-display";
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: saree?.name || "",
    price: saree?.price?.toString() || "",
    description: saree?.description || "",
    fabric: saree?.fabric || t.fabrics[0],
    shopify_url: saree?.shopify_url || "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(saree?.image_url || null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && !file) { toast.error("Please choose an image"); return; }
    setBusy(true);
    try {
      let image_path = saree?.image_path;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const up = await api.post("/uploads/image?folder=sarees", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_path = up.data.path;
      }
      const body = { name: form.name, price: parseFloat(form.price), description: form.description, fabric: form.fabric, image_path, shopify_url: form.shopify_url };
      if (isEdit) {
        await api.patch(`/sarees/${saree.id}`, body);
        toast.success(t.dash.updated);
      } else {
        await api.post("/sarees", body);
        toast.success("Saree added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={isEdit ? "edit-saree-modal" : "add-saree-modal"}>
        <form onSubmit={submit} className="p-6 sm:p-10">
          <h2 className={`${head} text-2xl sm:text-3xl text-[#2D1820] font-light`}>{isEdit ? t.dash.edit : t.dash.add}</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.photo}</label>
              <label className="mt-2 block border-2 border-dashed border-[#E8DFDC] hover:border-[#C9A84C] cursor-pointer aspect-[16/9] sm:aspect-[3/1] bg-[#FCFAF8] overflow-hidden" data-testid="saree-photo-dropzone">
                {preview ? (<img src={preview} alt="preview" className="w-full h-full object-cover" />) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#635057]"><Upload size={22} /><div className="text-xs tracking-widest uppercase mt-2">Tap to upload</div></div>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="saree-photo-input" />
              </label>
            </div>
            <div><label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.name}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field mt-2" data-testid="saree-name-input" /></div>
            <div><label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.price}</label>
              <input required type="number" min="0" step="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="field mt-2" data-testid="saree-price-input" /></div>
            <div className="sm:col-span-2"><label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.fabric}</label>
              <select value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} className="field mt-2" data-testid="saree-fabric-select">
                {t.fabrics.map((f) => <option key={f}>{f}</option>)}
              </select></div>
            <div className="sm:col-span-2"><label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.description}</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="field mt-2" data-testid="saree-desc-input" /></div>
            <div className="sm:col-span-2"><label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.dash.shopifyUrl}</label>
              <input type="url" value={form.shopify_url} onChange={(e) => setForm({ ...form, shopify_url: e.target.value })} placeholder="https://yourstore.myshopify.com/products/royal-maroon-silk" className="field mt-2" data-testid="saree-shopify-input" />
              <div className="text-[11px] text-[#635057] mt-1">{t.dash.shopifyHelp}</div></div>
          </div>
          <div className="mt-8 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="saree-cancel-btn">Cancel</button>
            <button disabled={busy} className="btn-primary" data-testid="saree-submit-btn">{busy ? t.dash.saving : t.dash.save}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
